import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { Box, Button, ButtonBase, Stack, Typography, Divider, Badge } from '@mui/material';
import React from 'react';
import { useSnackbar } from 'notistack';
import Logo from '../../../assets/brand-logo.png';
import Image from '../../../components/Image';
import Chat from '../../../assets/chat.png';
import Iconify from '../../../components/Iconify';
import { openModal } from '../../../redux/slices/authUiSlice';
import useResponsive from '../../../hooks/useResponsive';
import NewLanguagePopover from './NewLanguagePopover';
import LanguagePopover from './LanguagePopover';
import useAuth from '../../../hooks/useAuth';
import useLocales from '../../../hooks/useLocales';
import NotificationPopover from './NotificationPopover';
import { useSelector } from '../../../redux/store';
import { fNumberDecimal } from '../../../utils/formatNumber';
import { getUserMemberBalance } from '../../../redux/slices/lookup';
import { openConfirm } from '../../../redux/slices/confirm';
import DownloadAppAlert from './DownloadAppAlert';

const DashboardHeader = ({ setOpenAccDrawer, openAccDrawer }) => {
  const { isAuthenticated, userProfile, logout } = useAuth();

  const { translate } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const isDesktop = useResponsive('up', 'lg');

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { memberBalance, showDownloadApp } = useSelector((x) => ({ ...x.lookup, ...x.settings }));

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const openChat = () => {
    if (window.LiveChatWidget) {
      setTimeout(() => {
        window.LiveChatWidget.call('maximize');
      }, 100);
    }
  };

  return (
    <Box>
      {showDownloadApp && !isDesktop && (
        <DownloadAppAlert navigate={navigate} translate={translate} />
      )}
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(180deg, #3f6b3f 0%, #1e3d2b 100%)',
          height: { xs: 50, sm: 90 },
          padding: {
            lg: '1.5vh 1vw',
          },
          paddingX: {
            xs: '14px',
          },
          paddingY: {
            xs: '4px',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Image
            src={Logo}
            onClick={() => navigate('/')}
            sx={{
              cursor: 'pointer',
              width: {
                lg: 200,
                xs: 100,
              },
            }}
          />

          {!isDesktop && (
            <Box
              onClick={() => openChat()}
              sx={{
                cursor: 'pointer',
                width: 28,
                height: 28,
                borderRadius: '5px',
                backgroundColor: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image src={Chat} sx={{ width: '18px', height: '18px' }} />
            </Box>
          )}
        </Box>

        <Stack direction="row" spacing={'.5rem'} alignItems="center">
          {/* Message Icon */}
          {isDesktop && (
            <Box
              onClick={() => openChat()}
              sx={{
                cursor: 'pointer',
                width: 40,
                height: 40,
                borderRadius: '5px',
                backgroundColor: 'hsla(0,0%,100%,.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image src={Chat} sx={{ width: '18px', height: '18px' }} />
            </Box>
          )}

          {isAuthenticated ? (
            <>
              {isDesktop && (
                <Stack direction={'row'} spacing={1} alignItems="center">
                  <ButtonBase onClick={() => setOpenAccDrawer(!openAccDrawer)}>
                    <Badge color="error" variant="dot" invisible={userProfile?.is_kyc_verified}>
                      <Typography sx={{ color: '#fff', fontWeight: 700, textTransform: 'uppercase' }}>
                        {translate(`${translate('hi')}, ${userProfile?.username || '-'}`)}
                      </Typography>
                    </Badge>
                    <Iconify icon="iconoir:nav-arrow-down" sx={{ color: '#fff', width: '25px', height: '25px' }} />
                  </ButtonBase>

                  <Typography sx={{ fontWeight: 700 }}>{`${translate('credit')}: ${
                    memberBalance?.currencyCode
                  } ${fNumberDecimal(memberBalance?.displayBalance)}`}</Typography>

                  <Iconify
                    onClick={() => getUserMemberBalance()}
                    icon="mynaui:refresh-solid"
                    sx={{ color: '#f1c16d', width: '16px', height: '16px', cursor: 'pointer' }}
                  />

                  <Divider sx={{ borderColor: '#fff', height: '20px', borderLeftWidth: '1px' }} />

                  <ButtonBase
                    onClick={() => navigate('/user/wallet?tab=withrdraw')}
                    sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}
                  >
                    {translate('withdraw')}
                  </ButtonBase>
                </Stack>
              )}
              <Button
                onClick={() => navigate('/user/wallet?tab=deposit')}
                variant="contained"
                sx={{
                  height: {
                    sm: 35,
                    xs: 29,
                  },
                  fontWeight: 500,
                  borderColor: '#f0c56b',
                  borderRadius: '6px',
                  color: '#000000',
                  background: 'linear-gradient(180deg,#ffe08d,#e09944)',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: {
                    xs: '12px',
                    lg: '16px',
                  },
                  p: { xs: '0 10px' },
                  textTransform: 'uppercase',
                  '&:hover': {
                    borderColor: '#ffd47a',
                    backgroundColor: 'rgba(255,212,122,0.08)',
                  },
                }}
              >
                {translate('deposit')}
              </Button>

              {!isDesktop && (
                <>
                  <Stack direction={'row'} alignItems="center" spacing={1}>
                    <Typography sx={{ color: '#f1c16d', fontSize: { sm: '1rem', xs: '.75rem' } }}>
                      {fNumberDecimal(memberBalance?.displayBalance)}
                    </Typography>
                    <Iconify
                      onClick={() => getUserMemberBalance()}
                      icon="mynaui:refresh-solid"
                      sx={{ color: '#f1c16d', width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                  </Stack>
                  <NotificationPopover />
                </>
              )}
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  dispatch(
                    openModal({
                      view: 'login',
                    })
                  );
                }}
                variant="outlined"
                sx={{
                  height: {
                    lg: 35,
                  },
                  fontWeight: 400,
                  color: '#f0c56b',
                  borderColor: '#f0c56b',
                  borderRadius: '6px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: {
                    xs: '0.7rem',
                    lg: '16px',
                  },
                  letterSpacing: '0.5px',
                  '&:hover': {
                    borderColor: '#ffd47a',
                    backgroundColor: 'rgba(255,212,122,0.08)',
                  },
                }}
              >
                {translate('login')}
              </Button>

              <Button
                onClick={() => {
                  dispatch(
                    openModal({
                      view: 'register',
                    })
                  );
                }}
                variant="contained"
                sx={{
                  height: {
                    lg: 35,
                  },
                  fontWeight: 400,
                  borderColor: '#f0c56b',
                  borderRadius: '6px',
                  color: '#000000',
                  background: 'linear-gradient(180deg,#ffe08d,#e09944)',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: {
                    xs: '0.7rem',
                    lg: '16px',
                  },
                  letterSpacing: '0.5px',
                  '&:hover': {
                    borderColor: '#ffd47a',
                    backgroundColor: 'rgba(255,212,122,0.08)',
                  },
                }}
              >
                {translate('sign_up')}
              </Button>
            </>
          )}
          {isDesktop && isAuthenticated && (
            <Button
              onClick={() =>
                dispatch(
                  openConfirm({
                    title: translate('logout_confirmation'),
                    subTitle: translate('are_you_sure_to_logout'),
                    onConfirm: handleLogout,
                  })
                )
              }
              variant="outlined"
              sx={{
                height: {
                  sm: 35,
                  xs: 29,
                },
                fontWeight: 500,
                borderColor: '#f0c56b',
                borderRadius: '6px',
                color: '#f0c56b',
                // background: 'linear-gradient(180deg,#ffe08d,#e09944)',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '1rem',
                textTransform: 'uppercase',
                '&:hover': {
                  borderColor: '#ffd47a',
                  backgroundColor: 'rgba(255,212,122,0.08)',
                },
              }}
            >
              {translate('logout')}
            </Button>
          )}
          {isDesktop && <LanguagePopover />}
        </Stack>
      </Box>
    </Box>
  );
};

export default DashboardHeader;
